// Formato de las alertas en la UI: mismos textos que el Telegram
// (backend/app/services/price_alert_logic.py) para que el usuario lea lo
// mismo en los dos sitios.

export const OPERATOR_LABELS = {
  crossing: 'Cruce',
  crossing_up: 'Cruza arriba',
  crossing_down: 'Cruza abajo',
  greater_than: 'Mayor que',
  less_than: 'Menor que',
  entering_channel: 'Entra en canal',
  exiting_channel: 'Sale de canal',
  occurs: 'Ocurre',
}

// Operadores de la serie «Precio» (diseño §2.1), en el orden del desplegable.
export const PRICE_OPERATORS = [
  'crossing', 'crossing_up', 'crossing_down', 'greater_than', 'less_than', 'entering_channel', 'exiting_channel',
]
export const CHANNEL_OPERATORS = ['entering_channel', 'exiting_channel']

export const ACTIVATION_LABELS = {
  once: 'Una vez',
  once_per_bar: 'Una vez por vela',
  once_per_bar_close: 'Una vez por cierre de vela',
  always: 'Siempre',
}

// Activaciones de precio por ticks (§2.2).
export const ACTIVATION_OPTIONS = [
  { value: 'once', label: 'Una vez', help: 'Avisa la primera vez y se desactiva.' },
  { value: 'once_per_bar', label: 'Una vez por vela', help: 'Como mucho un aviso por vela del timeframe.' },
  { value: 'always', label: 'Siempre', help: 'Avisa cada vez que se cumple; un cruce exige volver al lado de partida.' },
]

// «Una vez por cierre de vela» (P2, F2): solo con el motor de cierre activo
// (engine_enabled del backend). Semántica de cierre (D-4): solo cierres, un
// toque exacto no es cruce.
export const CLOSE_ACTIVATION_OPTION = {
  value: 'once_per_bar_close',
  label: 'Una vez por cierre de vela',
  help: 'Se evalúa al cerrar cada vela, con su precio de cierre (un toque exacto no es cruce).',
}

export function priceActivationOptions(engineEnabled) {
  return engineEnabled ? [...ACTIVATION_OPTIONS, CLOSE_ACTIVATION_OPTION] : ACTIVATION_OPTIONS
}

// Eventos de indicador (F2): el backend solo los produce al cierre de vela,
// nunca intravela (invariante), así que solo hay estas dos.
export const EVENT_ACTIVATION_OPTIONS = [
  { value: 'once', label: 'Una vez', help: 'Avisa en el primer evento y se desactiva.' },
  { value: 'once_per_bar_close', label: 'Una vez por cierre de vela', help: 'Avisa en cada vela que cierra con el evento.' },
]

export const STATUS_LABELS = {
  active: 'Activa',
  paused: 'Pausada',
  triggered: 'Disparada',
  expired: 'Vencida',
  error: 'Error',
}

// Vencimiento estilo TradingView (D-11).
export const EXPIRY_OPTIONS = [
  { value: 'indefinite', label: 'Indefinida' },
  { value: 'daily_close', label: 'Cierre diario' },
  { value: '1w', label: '1 semana' },
  { value: '1m', label: '1 mes' },
  { value: 'custom', label: 'Fecha personalizada' },
]

export const FEED_LABELS = { bingx: 'BingX (perpetuo)' }
export const DEFAULT_NAME = 'Precio'

// 'BTC/USDT:USDT' | 'BTC/USDT' | 'BTCUSDT' -> 'BTCUSDT'
export function compactSymbol(symbol) {
  return String(symbol || '').split(':')[0].replace(/[/\-_]/g, '').replace(/\.P$/i, '').toUpperCase()
}

export function sameSymbol(a, b) {
  return compactSymbol(a) === compactSymbol(b)
}

// 68512.4 -> '68.512,40' con los decimales del contrato (price_decimals).
export function formatAlertPrice(value, decimals = 2) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  const d = Math.max(0, Math.min(12, Number.isInteger(decimals) ? decimals : 2))
  return n.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d, useGrouping: 'always' })
}

// 'Cruza arriba 70.000,0' · 'Entra en canal 69.000,0 – 71.000,0'
export function conditionText(operator, value1, value2, decimals) {
  const label = OPERATOR_LABELS[operator] || operator
  if (CHANNEL_OPERATORS.includes(operator)) {
    return `${label} ${formatAlertPrice(value1, decimals)} – ${formatAlertPrice(value2, decimals)}`
  }
  return `${label} ${formatAlertPrice(value1, decimals)}`
}

// Texto de la condición de una alerta o un aviso: el que escribe el backend
// (condition_label: «SHOWICT Quantum: BOS alcista (A+)»), con el de precio
// como respaldo si no viene.
export function alertConditionText(a) {
  return a?.condition_label || conditionText(a?.operator, a?.value_1, a?.value_2, a?.price_decimals)
}

// Mensaje por defecto estilo TradingView (D-8): «{SÍMBOLO} {condición}
// {valor}». Mismo texto que default_message del backend.
export function defaultMessage(symbol, operator, value1, value2, decimals) {
  return `${compactSymbol(symbol)} ${conditionText(operator, value1, value2, decimals)}`
}

// 'BingX (perpetuo) · 4h' — etiqueta de feed obligatoria (A-8).
export function feedText(feed, timeframe) {
  const label = FEED_LABELS[feed] || feed
  return timeframe ? `${label} · ${timeframe}` : label
}

// Valor por encima del precio actual -> cruza arriba; por debajo -> abajo.
export function suggestOperator(target, current) {
  if (target === null || target === undefined || target === '' || current === null || current === undefined) return 'crossing_up'
  const t = Number(target)
  const c = Number(current)
  if (!Number.isFinite(t) || !Number.isFinite(c) || t === c) return 'crossing_up'
  return t > c ? 'crossing_up' : 'crossing_down'
}

// Zona IANA del navegador para la hora local del Telegram (display_tz).
export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}
